import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';

import {
  WalletBatchItem,
  WalletBatchItemsFilters,
  WalletBatchItemsPage,
  WalletNavigation,
  WalletObservationKind,
  WalletReportColumnConfig,
  WalletReportConfig,
  WalletReportDownloadUrl,
  WalletReportExport,
  WalletReportExportsPage,
  WalletReportPreview,
  WalletReportPreviewColumn,
  WalletReportTemplate,
  WalletReportsCatalog,
} from 'src/app/models/WalletReports';
import { buildCsvBr, columnAlign, columnLetter } from 'src/app/pages/wallet/reports/report-format';
import { REPORTS_CATALOG, REPORT_TYPE_FILE_PREFIX } from 'src/app/pages/wallet/reports/report-defaults';

/** Atraso artificial do mock — sente como rede de verdade sem travar o teste (0 nos specs via `MOCK_LATENCY_MS`). */
export const MOCK_LATENCY_MS = 300;

const SEEDED_NAMES = ['Maria Souza', 'João Pereira', 'Ana Lima', 'Carlos Dias', 'Fernanda Rocha', 'Pedro Alves', 'Juliana Mota', 'Rafael Nunes'];
const SEEDED_CARRIERS = ['Vivo', 'Claro', 'TIM', 'Oi'];

/**
 * Dados simulados dos endpoints de `/wallet/navigation`, `/wallet/entries/{id}/items` e
 * `/wallet/reports/*` (M7, task 17tm466nz8b) — gerador determinístico (mesma semente ⇒
 * mesmo resultado, sem `Math.random()` cru) para a prévia não "tremer" a cada detecção de
 * mudança e os specs não ficarem flakey. Troca para a API real: ver `WalletReportsService`.
 */
@Injectable({ providedIn: 'root' })
export class WalletReportsMockService {

  /** Exportações em memória — some ao recarregar a página, como qualquer mock de sessão. */
  private exports: WalletReportExport[] = [];

  /** Modelos salvos em memória, com os 2 exemplos do mockup como ponto de partida. */
  private templates: WalletReportTemplate[] = [
    {
      id: 'tpl-1',
      name: 'Disparos do mês (SMS)',
      type: 'dispatches',
      config: {
        filters: { ...emptyFilters(), channels: ['sms'], period: 'this_month' },
        columns: columnsFor('dispatches'),
        format: 'csv_br',
      },
      updated_at: '2026-10-01T10:00:00Z',
    },
    {
      id: 'tpl-2',
      name: 'Recargas e notas fiscais',
      type: 'recharges',
      config: { filters: { ...emptyFilters(), period: 'last_30_days' }, columns: columnsFor('recharges'), format: 'xlsx' },
      updated_at: '2026-09-20T10:00:00Z',
    },
  ];

  navigation(): Observable<WalletNavigation> {
    return of<WalletNavigation>({
      reports_enabled: true,
      wallet_migrated: { sms: true, call: false },
      hide: { messages_report: true, lead_responses: false, recharges: false },
    }).pipe(delay(MOCK_LATENCY_MS));
  }

  catalog(): Observable<WalletReportsCatalog> {
    return of(REPORTS_CATALOG).pipe(delay(MOCK_LATENCY_MS));
  }

  batchItems(entryId: string, filters: WalletBatchItemsFilters): Observable<WalletBatchItemsPage> {
    const total = 250;
    const perPage = filters.per_page || 25;
    const page = filters.page || 1;
    const all = buildBatchItems(entryId, total);
    const filtered = filters.q ? all.filter((item) => matchesQuery(item, filters.q as string)) : all;
    const start = (page - 1) * perPage;

    return of<WalletBatchItemsPage>({
      entry_id: entryId,
      kind: 'sms_chunk',
      channel: 'sms',
      total,
      found: filtered.length,
      detail_complete: true,
      unit_cost_micros: 89333,
      page,
      per_page: perPage,
      last_page: Math.max(1, Math.ceil(filtered.length / perPage)),
      data: filtered.slice(start, start + perPage),
    }).pipe(delay(MOCK_LATENCY_MS));
  }

  preview(config: WalletReportConfig): Observable<WalletReportPreview> {
    const def = REPORTS_CATALOG.types.find((item) => item.key === config.type) || REPORTS_CATALOG.types[0];
    const visible = config.columns.filter((column) => column.visible);
    const columns: WalletReportPreviewColumn[] = visible.map((column, index) => {
      const columnDef = def.columns.find((item) => item.key === column.key);
      return {
        letter: columnLetter(index),
        key: column.key,
        label: column.label,
        type: columnDef ? columnDef.type : 'text',
        align: columnAlign(columnDef ? columnDef.type : 'text'),
      };
    });

    const days = periodDays(config.filters.from, config.filters.to);
    const perDay = perDayRowCount(config.type);
    const estimatedRows = Math.max(1, Math.round(days * perDay * channelFactor(config)));
    const previewCount = Math.min(REPORTS_CATALOG.limits.preview_rows, estimatedRows, 14);
    const rows = buildPreviewRows(config.type, visible, previewCount);
    const headers = visible.map((column) => column.label);
    const csvSample = buildCsvBr(headers, rows.slice(0, 6));

    return of<WalletReportPreview>({
      columns,
      rows,
      csv_sample: csvSample,
      estimate: {
        rows: estimatedRows,
        exact: estimatedRows <= REPORTS_CATALOG.limits.immediate_rows,
        mode: estimatedRows <= REPORTS_CATALOG.limits.immediate_rows ? 'immediate' : 'background',
      },
      partial: config.type === 'consumption' && days > 31,
    }).pipe(delay(MOCK_LATENCY_MS));
  }

  createExport(config: WalletReportConfig): Observable<WalletReportExport> {
    const days = periodDays(config.filters.from, config.filters.to);
    const estimatedRows = Math.max(1, Math.round(days * perDayRowCount(config.type) * channelFactor(config)));
    const immediate = estimatedRows <= REPORTS_CATALOG.limits.immediate_rows;

    const item: WalletReportExport = {
      id: `exp-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      type: config.type,
      type_label: (REPORTS_CATALOG.types.find((t) => t.key === config.type) || REPORTS_CATALOG.types[0]).label,
      format: config.format,
      status: 'queued',
      mode: immediate ? 'immediate' : 'background',
      estimated_rows: estimatedRows,
      row_count: null,
      file_name: null,
      file_size: null,
      error: null,
      params_summary: periodSummary(config),
      params: { type: config.type, filters: config.filters, columns: config.columns, format: config.format },
      created_at: new Date().toISOString(),
      finished_at: null,
      expires_at: null,
    };

    this.exports = [item, ...this.exports];

    return of(item).pipe(delay(immediate ? MOCK_LATENCY_MS : MOCK_LATENCY_MS * 2));
  }

  listExports(): Observable<WalletReportExportsPage> {
    // Cada consulta avança um passo as que ainda estão rodando — simula o job terminando
    // sem precisar de um timer próprio: quem quer ver o fim faz polling, como no real.
    this.exports = this.exports.map((item) => advanceExport(item));

    return of<WalletReportExportsPage>({ data: this.exports, meta: { page: 1, per_page: 20, total: this.exports.length } })
      .pipe(delay(MOCK_LATENCY_MS));
  }

  getExport(id: string): Observable<WalletReportExport | null> {
    return this.listExports().pipe(map((page) => page.data.find((item) => item.id === id) || null));
  }

  downloadUrl(id: string): Observable<WalletReportDownloadUrl> {
    const item = this.exports.find((entry) => entry.id === id);
    const headers = item?.params?.columns.filter((c) => c.visible).map((c) => c.label) || [];
    const rows = item ? buildPreviewRows(item.type, item.params?.columns.filter((c) => c.visible) || [], 20) : [];
    const csv = buildCsvBr(headers, rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    return of<WalletReportDownloadUrl>({ url, expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString() })
      .pipe(delay(MOCK_LATENCY_MS));
  }

  deleteExport(id: string): Observable<void> {
    this.exports = this.exports.filter((item) => item.id !== id);
    return of(undefined).pipe(delay(MOCK_LATENCY_MS));
  }

  listTemplates(type?: string): Observable<{ data: WalletReportTemplate[] }> {
    const data = type ? this.templates.filter((item) => item.type === type) : this.templates;
    return of({ data }).pipe(delay(MOCK_LATENCY_MS));
  }

  createTemplate(name: string, type: WalletReportTemplate['type'], config: WalletReportTemplate['config']): Observable<WalletReportTemplate> {
    const template: WalletReportTemplate = { id: `tpl-${Date.now()}`, name, type, config, updated_at: new Date().toISOString() };
    this.templates = [template, ...this.templates];
    return of(template).pipe(delay(MOCK_LATENCY_MS));
  }

  deleteTemplate(id: string): Observable<void> {
    this.templates = this.templates.filter((item) => item.id !== id);
    return of(undefined).pipe(delay(MOCK_LATENCY_MS));
  }
}

function emptyFilters() {
  return { from: null, to: null, channels: [], origins: [], statuses: [], entry_types: [], services: [], payment_statuses: [], kinds: [], invoice_statuses: [], group_by: null, split_by_origin: false, entry_ids: [] };
}

function columnsFor(type: string): WalletReportColumnConfig[] {
  const def = REPORTS_CATALOG.types.find((item) => item.key === type) || REPORTS_CATALOG.types[0];
  return def.columns.map((column) => ({ key: column.key, label: column.label, visible: column.default }));
}

function periodDays(from: string | null, to: string | null): number {
  if (!from || !to) { return 30; }
  const start = new Date(`${from}T00:00:00Z`).getTime();
  const end = new Date(`${to}T00:00:00Z`).getTime();
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
}

function perDayRowCount(type: string): number {
  switch (type) {
    case 'dispatches': return 1200;
    case 'entries': return 90;
    case 'recharges': return 3;
    case 'consumption': return 4;
    default: return 50;
  }
}

function channelFactor(config: WalletReportConfig): number {
  if (!config.filters.channels.length) { return 1; }
  return Math.max(0.15, config.filters.channels.length / 3);
}

function periodSummary(config: WalletReportConfig): string {
  const from = config.filters.from ? formatDateBr(config.filters.from) : null;
  const to = config.filters.to ? formatDateBr(config.filters.to) : null;
  const period = from && to ? `${from} a ${to}` : 'Período completo';
  const channels = config.filters.channels.length ? ` · ${config.filters.channels.join(', ')}` : '';
  return `${period}${channels}`;
}

function formatDateBr(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

/** Avança o status do mock a cada consulta: queued → running → ready, sem timer próprio. */
function advanceExport(item: WalletReportExport): WalletReportExport {
  if (item.status === 'queued') {
    return { ...item, status: 'running' };
  }

  if (item.status === 'running') {
    const rows = item.estimated_rows || 100;
    return {
      ...item,
      status: 'ready',
      row_count: rows,
      file_name: `smsfunnel-${REPORT_TYPE_FILE_PREFIX[item.type]}.${item.format === 'xlsx' ? 'xlsx' : 'csv'}`,
      file_size: rows * 180,
      finished_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 7 * 86400000).toISOString(),
    };
  }

  return item;
}

function buildBatchItems(entryId: string, total: number): WalletBatchItem[] {
  const items: WalletBatchItem[] = [];
  const seed = hashSeed(entryId);

  for (let index = 0; index < total; index += 1) {
    const name = SEEDED_NAMES[(seed + index) % SEEDED_NAMES.length];
    items.push({
      position: index + 1,
      sent_at: new Date(Date.UTC(2026, 9, 14, 12, 30, (index * 3) % 60)).toISOString(),
      destination: `(11) 9****-${String(1000 + ((seed + index * 7) % 9000)).padStart(4, '0')}`,
      lead_name: name,
      text: `Oi ${name.split(' ')[0]}, sua oferta expira hoje…`,
      origin_name: 'Funil Black Friday',
      step: 2,
    });
  }

  return items;
}

function matchesQuery(item: WalletBatchItem, query: string): boolean {
  const normalized = normalizeForSearch(query);
  return [item.lead_name || '', item.destination, item.text].some((value) => normalizeForSearch(value).includes(normalized));
}

function normalizeForSearch(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) { hash = (hash * 31 + value.charCodeAt(i)) >>> 0; }
  return hash;
}

function buildPreviewRows(type: string, columns: WalletReportColumnConfig[], count: number): string[][] {
  const rows: string[][] = [];

  for (let index = 0; index < count; index += 1) {
    rows.push(columns.map((column) => previewCell(type, column.key, index)));
  }

  return rows;
}

/** Célula de prévia já "formatada" (string), do mesmo jeito que o back devolve em `preview.rows`. */
function previewCell(type: string, key: WalletObservationKind | string, index: number): string {
  const name = SEEDED_NAMES[index % SEEDED_NAMES.length];
  const date = new Date(Date.UTC(2026, 9, 14, 9 + (index % 10), 31, 0));
  const datetime = formatDatetimeBr(date);

  const byKey: { [key: string]: string } = {
    charged_at: datetime,
    sent_at: datetime,
    created_at: datetime,
    confirmed_at: datetime,
    channel: ['SMS', 'WhatsApp', 'Ligação'][index % 3],
    origin: ['Automação', 'Disparo em massa', 'Conversa'][index % 3],
    origin_name: 'Funil Black Friday',
    step: String(1 + (index % 4)),
    template: 'boas_vindas_v2',
    message: `Oi ${name.split(' ')[0]}, sua oferta expira hoje`,
    phone: `(11) 9${String(1000 + index * 37).padStart(4, '0').slice(0, 4)}-${String(1000 + index * 13).padStart(4, '0')}`,
    lead_name: name,
    delivery_status: ['Entregue', 'Enviado', 'Falhou'][index % 3],
    failure_reason: index % 3 === 2 ? 'Número inválido' : '',
    call_duration: String(30 + (index % 5) * 15),
    quantity: String(1 + (index % 3)),
    entry_id: String(88000 + index),
    batch_size: '250',
    unit_cost: '0,089',
    amount: (0.08 + index * 0.01).toFixed(3).replace('.', ','),
    entry_amount: (22.25).toFixed(2).replace('.', ','),
    entry_type: ['Débito', 'Crédito', 'Bônus'][index % 3],
    service: ['sms', 'whatsapp', 'call', 'ai'][index % 4],
    unit: 'mensagem',
    observation: `Lote de 250 SMS · Automação`,
    invoice_url: 'https://nfe.exemplo/imprimir/123',
    balance_after: (1305.12 - index).toFixed(2).replace('.', ','),
    recharge_id: `3f1${index}`,
    kind: 'Por valor',
    payment_type: ['PIX', 'Cartão', 'Boleto'][index % 3],
    payment_status: 'Pago',
    legacy_credits: '0',
    invoice_status: 'Emitida',
    paid_amount: (200).toFixed(2).replace('.', ','),
    credited_amount: (200).toFixed(2).replace('.', ','),
    bonus_amount: (10).toFixed(2).replace('.', ','),
    period: formatDateBr(dateToIso(date)),
    entries: String(10 + index),
    units: String(100 + index * 4),
  };

  return byKey[key] ?? '';
}

function formatDatetimeBr(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

function dateToIso(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}
